import { Image as PhotoIcon } from 'lucide-react';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';

// Soft Card upload panel — pure UI for two-side image uploads (Photo
// ID, Insurance card). Drives drag/drop and lets the patient skip when
// nothing's available. Auth-agnostic — `onUpload` and `onSkip` are
// supplied by the caller.

export type UploadKind = 'photoId' | 'insuranceCard';
export type UploadStatus = 'ready' | 'submitting' | 'submitted' | 'error';
export type SidePreview = { url: string; uploading: boolean; error: string | null };

const verbiage = {
  en: {
    photoId: {
      title: 'Photo ID',
      description: 'Upload a clear photo of your official ID so we can verify your identity.',
      submitted: 'Photo ID received',
      submittedDetail: 'Thank you. Your ID is on file.',
    },
    insuranceCard: {
      title: 'Insurance card',
      description: 'Upload the front and back of your insurance card.',
      submitted: 'Insurance card received',
      submittedDetail: 'Thank you. Your card is on file.',
    },
    front: 'Front',
    back: 'Back',
    tap: 'Tap to upload',
    ordrop: 'or drag and drop',
    maxsize: 'PNG, JPG, GIF (max 10MB)',
    done: 'Done',
    skip: 'Skip',
    tryagain: 'Try again',
    errorTitle: 'Something went wrong',
    errorDetail: 'Please try again in a moment.',
    backLink: 'Back',
    uploadFailed: 'Upload failed. Please try again.',
  },
  es: {
    photoId: {
      title: 'Identificación con foto',
      description: 'Suba una foto clara de su identificación oficial para verificar su identidad.',
      submitted: 'Identificación recibida',
      submittedDetail: 'Gracias.',
    },
    insuranceCard: {
      title: 'Tarjeta de seguro',
      description: 'Suba la parte frontal y posterior de su tarjeta de seguro.',
      submitted: 'Tarjeta recibida',
      submittedDetail: 'Gracias.',
    },
    front: 'Frente',
    back: 'Reverso',
    tap: 'Toque para subir',
    ordrop: 'o arrastre',
    maxsize: 'PNG, JPG, GIF (max 10MB)',
    done: 'Listo',
    skip: 'Saltar',
    tryagain: 'Intenta otra vez',
    errorTitle: 'Algo salió mal',
    errorDetail: 'Por favor inténtelo de nuevo en unos momentos.',
    backLink: 'Volver',
    uploadFailed: 'No se pudo subir. Intente de nuevo.',
  },
};

type Props = {
  kind: UploadKind;
  primaryColor: string;
  language: 'en' | 'es';
  status?: UploadStatus;
  // Upload happens immediately when the user picks a file. Caller is
  // responsible for the actual S3 POST. Throwing surfaces an error
  // tile under the affected side preview.
  onUpload: (side: 'front' | 'back', file: File) => Promise<void>;
  // Called when patient confirms they're done with whatever sides they
  // uploaded. The runner uses this to advance.
  onDone: () => void;
  // Skip the step entirely (server marks complete with `skip=true`).
  onSkip: () => Promise<void> | void;
  // Optional top "Back" link.
  showTopBack?: boolean;
  topBackHref?: string;
  exitLabel?: string;
  onExit?: () => void;
};

export const SoftUploadPanel: React.FC<Props> = ({
  kind,
  primaryColor,
  language,
  status = 'ready',
  onUpload,
  onDone,
  onSkip,
  showTopBack,
  topBackHref,
  exitLabel,
  onExit,
}) => {
  const v = verbiage[language];
  const k = kind === 'photoId' ? v.photoId : v.insuranceCard;

  const [front, setFront] = useState<SidePreview | null>(null);
  const [back, setBack] = useState<SidePreview | null>(null);

  const handleSidePick = async (side: 'front' | 'back', file: File) => {
    const setSide = side === 'front' ? setFront : setBack;
    const objectUrl = URL.createObjectURL(file);

    setSide({ url: objectUrl, uploading: true, error: null });

    try {
      await onUpload(side, file);
      setSide(prev => (prev ? { ...prev, uploading: false } : prev));
    } catch (err) {
      Sentry.captureException(err);
      // Keep the optimistic preview URL so the user can see *what*
      // failed and the error overlay renders on top of it. The prior
      // version cleared `url` to '', which fell through the
      // `{preview?.url ? ... : ...}` branch in <UploadSide> and showed
      // the empty "tap to upload" placeholder instead of the error —
      // exactly the "it goes away" symptom.
      setSide({ url: objectUrl, uploading: false, error: v.uploadFailed });
    }
  };

  const hasAnyUpload = !!(front?.url && !front.error) || !!(back?.url && !back.error);

  if (status === 'submitted') {
    return (
      <div
        className="rounded-3xl overflow-hidden p-8 text-center flex flex-col items-center gap-3 dark:ring-1 dark:ring-white/10"
        style={{ backgroundColor: `${primaryColor}12` }}
      >
        <div
          className="size-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${primaryColor}25`, color: primaryColor }}
          aria-hidden="true"
        >
          <Icon icon={Icons.checkCircle} className="size-7" />
        </div>
        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{k.submitted}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{k.submittedDetail}</p>
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="mt-2 inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {exitLabel ?? v.backLink}
          </button>
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="portal-card p-6 text-center">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {v.errorTitle}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{v.errorDetail}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {showTopBack && topBackHref && (
        <div className="mb-4">
          <a
            href={topBackHref}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <Icon icon={Icons.chevronLeft} className="size-4" />
            {v.backLink}
          </a>
        </div>
      )}

      <div className="portal-card p-5 flex flex-col gap-5">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
            {k.title}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-prose">
            {k.description}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <UploadSide
            label={v.front}
            preview={front}
            primaryColor={primaryColor}
            tap={v.tap}
            ordrop={v.ordrop}
            maxsize={v.maxsize}
            tryagain={v.tryagain}
            onPick={file => handleSidePick('front', file)}
          />
          <UploadSide
            label={v.back}
            preview={back}
            primaryColor={primaryColor}
            tap={v.tap}
            ordrop={v.ordrop}
            maxsize={v.maxsize}
            tryagain={v.tryagain}
            onPick={file => handleSidePick('back', file)}
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-950/5 dark:border-white/10">
          {!hasAnyUpload && (
            <button
              type="button"
              onClick={() => onSkip()}
              disabled={status === 'submitting'}
              className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-white/15 disabled:opacity-50"
            >
              {v.skip}
            </button>
          )}
          <button
            type="button"
            onClick={onDone}
            disabled={!hasAnyUpload || status === 'submitting'}
            className="inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
          >
            {v.done}
          </button>
        </div>
      </div>
    </div>
  );
};

const UploadSide: React.FC<{
  label: string;
  preview: SidePreview | null;
  primaryColor: string;
  tap: string;
  ordrop: string;
  maxsize: string;
  tryagain: string;
  onPick: (file: File) => void | Promise<void>;
}> = ({ label, preview, primaryColor, tap, ordrop, maxsize, tryagain, onPick }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: files => {
      if (files?.[0]) onPick(files[0]);
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
  });

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <div
        {...getRootProps()}
        className="relative flex flex-col items-center justify-center min-h-44 rounded-xl border-2 border-dashed cursor-pointer p-4 transition-colors"
        style={{ borderColor: isDragActive ? primaryColor : undefined }}
      >
        <input {...getInputProps()} />
        {preview?.url ? (
          <>
            <img
              className="max-h-44 w-auto rounded-md object-contain"
              src={preview.url}
              alt={label}
            />
            {preview.uploading && (
              <div
                className="absolute inset-0 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}99` }}
              >
                <span className="text-white text-sm font-medium animate-pulse">…</span>
              </div>
            )}
            {preview.error && (
              <p className="absolute bottom-1 inset-x-2 text-xs text-red-600 text-center">
                {tryagain}
              </p>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center text-center gap-2">
            <PhotoIcon className="size-10 text-gray-400 dark:text-gray-500" />
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold" style={{ color: primaryColor }}>
                {tap}
              </span>{' '}
              {ordrop}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{maxsize}</p>
          </div>
        )}
      </div>
    </div>
  );
};
